/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ChartSeriesData, Info, ItemInfo } from '#utils/UNSAFE_visTypes/chart';
import {
  getNextItemInGroup,
  getNextItemInSeries,
  getPrevItemInGroup,
  getPrevItemInSeries
} from '#utils/PRIVATE_chartUtils/navUtils';
import { BarItemData } from '#UNSAFE_BarChart/barChart.types';
import { createStackedGroupNavigator } from './stackedGroupNavUtils';

type isItemNavigableType = (seriesIndex: number, groupIndex: number) => boolean;

/**
 * Get next item in unstacked bar chart.
 * @param info
 * @param viewportEndIndex
 * @param numSeries
 * @param isNavigable
 * @returns
 */
export function getNextItem(
  info: ItemInfo,
  viewportEndIndex: number,
  numSeries: number,
  isNavigable: isItemNavigableType
): ItemInfo | undefined {
  const nextItemInGroup = getNextItemInGroup(info, numSeries, isNavigable);
  if (nextItemInGroup) return nextItemInGroup;

  if (info.groupIndex < viewportEndIndex) {
    let nextItem: ItemInfo | undefined = { seriesIndex: 0, groupIndex: info.groupIndex + 1 };
    if (isNavigable(nextItem.seriesIndex, nextItem.groupIndex)) return nextItem;
    nextItem = getNextItem(nextItem, viewportEndIndex, numSeries, isNavigable);
    if (nextItem) return nextItem;
  }
  return;
}

/**
 * Get prev item in unstacked bar chart.
 * @param info
 * @param viewportStartIndex
 * @param numSeries
 * @param isNavigable
 * @returns
 */
export function getPreviousItem(
  info: ItemInfo,
  viewportStartIndex: number,
  numSeries: number,
  isNavigable: (seriesIndex: number, groupIndex: number) => boolean
) {
  const prevItemInGroup = getPrevItemInGroup(info, isNavigable);
  if (prevItemInGroup) return prevItemInGroup;

  if (info.groupIndex > viewportStartIndex) {
    let prevItem = { groupIndex: info.groupIndex - 1, seriesIndex: numSeries - 1 };
    if (isNavigable(prevItem.seriesIndex, prevItem.groupIndex)) return prevItem;
    prevItem = getPreviousItem(prevItem, viewportStartIndex, numSeries, isNavigable);
    if (prevItem) return prevItem;
  }
  return info;
}

export function getBarChartNavUtil<K, D extends BarItemData<K>>(
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  series: ChartSeriesData<K, D>[],
  numSeries: number,
  viewportStartIndex: number,
  viewportEndIndex: number,
  isStacked: boolean,
  isRtl: boolean,
  isHoriz: boolean,
  stackGroups: string[],
  getStackGroup: (series: ChartSeriesData<K, D>) => string | undefined
) {
  const isItemNavigable = (seriesIndex: number, groupIndex: number) => {
    const barItem = getDataItem(seriesIndex, groupIndex);
    return Boolean(barItem);
  };

  const getNextChartItem = (
    itemInfo: Info,
    arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
  ): Info => {
    if (
      !isStacked &&
      (((arrow === 'ArrowDown' || arrow === 'ArrowUp') && !isHoriz) ||
        ((arrow === 'ArrowLeft' || arrow === 'ArrowRight') && isHoriz))
    ) {
      return itemInfo;
    }

    let newInfo: Info | undefined;
    // TODO: this is just to keep typescript happy.
    // figure out consistent typing while doing type cleanup.
    const info = {
      groupIndex: itemInfo.groupIndex!,
      seriesIndex: itemInfo.seriesIndex!
    };

    if (info.groupIndex! < viewportStartIndex) {
      info.groupIndex = viewportStartIndex;
      return info;
    } else if (info.groupIndex! > viewportEndIndex) {
      info.groupIndex = viewportEndIndex;
      return info;
    }

    if (isStacked) {
      // Stacked-group navigation helpers moved to stackedGroupNavUtils
      const { moveWithinStack } = createStackedGroupNavigator(
        getDataItem,
        series,
        numSeries,
        stackGroups,
        getStackGroup,
        isItemNavigable
      );

      if ((arrow === 'ArrowLeft' && !isHoriz) || (arrow === 'ArrowUp' && isHoriz)) {
        newInfo =
          isRtl && !isHoriz
            ? getNextItemInSeries(info, viewportEndIndex, isItemNavigable)
            : getPrevItemInSeries(info, viewportStartIndex, isItemNavigable);
      } else if ((arrow === 'ArrowRight' && !isHoriz) || (arrow === 'ArrowDown' && isHoriz)) {
        newInfo =
          isRtl && !isHoriz
            ? getPrevItemInSeries(info, viewportStartIndex, isItemNavigable)
            : getNextItemInSeries(info, viewportEndIndex, isItemNavigable);
      } else if ((arrow === 'ArrowDown' && !isHoriz) || (arrow === 'ArrowLeft' && isHoriz)) {
        const dir: 'prev' | 'next' = isRtl && isHoriz ? 'next' : 'prev';
        newInfo = moveWithinStack(info, dir);
      } else if ((arrow === 'ArrowUp' && !isHoriz) || (arrow === 'ArrowRight' && isHoriz)) {
        const dir: 'prev' | 'next' = isRtl && isHoriz ? 'prev' : 'next';
        newInfo = moveWithinStack(info, dir);
      }
    } else {
      if (!isHoriz) {
        if (arrow === 'ArrowRight') {
          newInfo = isRtl
            ? getPreviousItem(info, viewportStartIndex, numSeries, isItemNavigable)
            : getNextItem(info, viewportEndIndex, numSeries, isItemNavigable);
        } else if (arrow === 'ArrowLeft') {
          newInfo = isRtl
            ? getNextItem(info, viewportEndIndex, numSeries, isItemNavigable)
            : getPreviousItem(info, viewportStartIndex, numSeries, isItemNavigable);
        }
      } else {
        if (arrow === 'ArrowDown') {
          newInfo = getNextItem(info, viewportEndIndex, numSeries, isItemNavigable);
        } else if (arrow === 'ArrowUp') {
          newInfo = getPreviousItem(info, viewportStartIndex, numSeries, isItemNavigable);
        }
      }
    }
    return newInfo || info;
  };

  return getNextChartItem;
}
